import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronRight, ChevronDown, Lock } from 'lucide-react';
import { getImageUrl } from '../utils/imageHelper';

// iter-356 — Excel-style spreadsheet grid for the Bulk Product Editor.
// Click a cell to select, drag/shift-click to select a rectangle,
// arrow/tab to navigate, type to edit, Ctrl+C / Ctrl+V to copy a value
// (or a whole rectangle) across many rows at once — the exact "same
// FSSAI number pasted into 40 rows" workflow this feature exists for.

const coerceValue = (raw, column) => {
  if (raw === undefined) return undefined;
  if (column.type === 'number') {
    if (raw === '' || raw === null) return null;
    const n = parseFloat(raw);
    return Number.isNaN(n) ? null : n;
  }
  if (column.type === 'boolean') {
    if (typeof raw === 'boolean') return raw;
    const s = String(raw).trim().toLowerCase();
    return ['true', 'yes', '1', 'y'].includes(s);
  }
  if (column.type === 'select') {
    const opts = column.options || [];
    const match = opts.find(o => {
      const val = typeof o === 'string' ? o : o.value;
      return String(val).toLowerCase() === String(raw).trim().toLowerCase();
    });
    return match ? (typeof match === 'string' ? match : match.value) : undefined;
  }
  return raw;
};

const isRowCellEditable = (row, column, categoriesMeta) => {
  if (row.kind === 'variant') {
    return ['sku', 'price', 'inventory', 'status'].includes(column.key);
  }
  if (column.type === 'readonly') return false;
  if (column.key === 'buying_mode' && categoriesMeta?.[row.category]?.buying_mode_override_enabled) return false;
  return true;
};

const optionLabel = (column, value) => {
  const opt = (column.options || []).find(o => (typeof o === 'string' ? o : o.value) === value);
  if (!opt) return value || '';
  return typeof opt === 'string' ? opt : opt.label;
};

const BulkEditorGrid = ({ columns, rows, dirtyKeys, onExpandToggle, onCellsChange, categoriesMeta }) => {
  const [selection, setSelection] = useState(null); // {r1,c1,r2,c2}
  const [editingCell, setEditingCell] = useState(null); // {r,c}
  const [editValue, setEditValue] = useState('');
  const draggingRef = useRef(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { if (editingCell && inputRef.current) inputRef.current.focus(); }, [editingCell]);

  const cellKey = (r, c) => `${rows[r]?.rowKey}::${columns[c]?.key}`;

  const commitEdit = useCallback((value) => {
    if (!editingCell) return;
    const { r, c } = editingCell;
    const row = rows[r];
    const column = columns[c];
    const coerced = coerceValue(value, column);
    if (coerced !== undefined) {
      onCellsChange([{ rowKey: row.rowKey, kind: row.kind, id: row.productId, combinationKey: row.combinationKey, colKey: column.key, value: coerced }]);
    }
    setEditingCell(null);
  }, [editingCell, rows, columns, onCellsChange]);

  const startEdit = (r, c, initialValue) => {
    const row = rows[r];
    const column = columns[c];
    if (!isRowCellEditable(row, column, categoriesMeta)) return;
    setSelection({ r1: r, c1: c, r2: r, c2: c });
    setEditingCell({ r, c });
    setEditValue(initialValue !== undefined ? initialValue : (row.values[column.key] ?? ''));
  };

  const handleMouseDown = (r, c, e) => {
    if (editingCell) commitEdit(editValue);
    draggingRef.current = true;
    setSelection({ r1: r, c1: c, r2: r, c2: c });
  };
  const handleMouseEnter = (r, c) => {
    if (draggingRef.current) setSelection(sel => sel ? { ...sel, r2: r, c2: c } : { r1: r, c1: c, r2: r, c2: c });
  };
  useEffect(() => {
    const up = () => { draggingRef.current = false; };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  const moveSelection = (dr, dc, extend) => {
    if (!selection) return;
    const nr = Math.max(0, Math.min(rows.length - 1, selection.r2 + dr));
    const nc = Math.max(0, Math.min(columns.length - 1, selection.c2 + dc));
    setSelection(extend ? { ...selection, r2: nr, c2: nc } : { r1: nr, c1: nc, r2: nr, c2: nc });
  };

  const handleKeyDown = (e) => {
    if (editingCell) {
      if (e.key === 'Enter') { e.preventDefault(); commitEdit(editValue); moveSelection(1, 0, false); }
      else if (e.key === 'Escape') { e.preventDefault(); setEditingCell(null); }
      else if (e.key === 'Tab') { e.preventDefault(); commitEdit(editValue); moveSelection(0, e.shiftKey ? -1 : 1, false); }
      return;
    }
    if (!selection) return;
    if (e.key === 'ArrowUp') { e.preventDefault(); moveSelection(-1, 0, e.shiftKey); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); moveSelection(1, 0, e.shiftKey); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); moveSelection(0, -1, e.shiftKey); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); moveSelection(0, 1, e.shiftKey); }
    else if (e.key === 'Tab') { e.preventDefault(); moveSelection(0, e.shiftKey ? -1 : 1, false); }
    else if (e.key === 'Enter') { e.preventDefault(); startEdit(selection.r2, selection.c2); }
    else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      const edits = [];
      const [minR, maxR] = [Math.min(selection.r1, selection.r2), Math.max(selection.r1, selection.r2)];
      const [minC, maxC] = [Math.min(selection.c1, selection.c2), Math.max(selection.c1, selection.c2)];
      for (let r = minR; r <= maxR; r++) {
        for (let c = minC; c <= maxC; c++) {
          const row = rows[r]; const column = columns[c];
          if (isRowCellEditable(row, column, categoriesMeta)) {
            edits.push({ rowKey: row.rowKey, kind: row.kind, id: row.productId, combinationKey: row.combinationKey, colKey: column.key, value: column.type === 'boolean' ? false : (column.type === 'number' ? null : '') });
          }
        }
      }
      if (edits.length) onCellsChange(edits);
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
      startEdit(selection.r2, selection.c2, e.key);
    }
  };

  // Copy / paste — attached at document level, scoped to when the grid
  // container currently owns keyboard focus.
  useEffect(() => {
    const inGrid = () => containerRef.current && containerRef.current.contains(document.activeElement);

    const onCopy = (e) => {
      if (!inGrid() || !selection) return;
      e.preventDefault();
      const [minR, maxR] = [Math.min(selection.r1, selection.r2), Math.max(selection.r1, selection.r2)];
      const [minC, maxC] = [Math.min(selection.c1, selection.c2), Math.max(selection.c1, selection.c2)];
      const lines = [];
      for (let r = minR; r <= maxR; r++) {
        const cells = [];
        for (let c = minC; c <= maxC; c++) {
          const row = rows[r]; const column = columns[c];
          const raw = row.values[column.key];
          cells.push(column.type === 'select' ? optionLabel(column, raw) : (raw ?? ''));
        }
        lines.push(cells.join('\t'));
      }
      e.clipboardData.setData('text/plain', lines.join('\n'));
    };

    const onPaste = (e) => {
      if (!inGrid() || !selection) return;
      e.preventDefault();
      const text = e.clipboardData.getData('text/plain');
      if (!text) return;
      const grid = text.replace(/\r/g, '').split('\n').filter((_, i, arr) => !(i === arr.length - 1 && arr[i] === '')).map(l => l.split('\t'));
      const minR = Math.min(selection.r1, selection.r2);
      const minC = Math.min(selection.c1, selection.c2);
      const maxR = Math.max(selection.r1, selection.r2);
      const maxC = Math.max(selection.c1, selection.c2);
      const singleValue = grid.length === 1 && grid[0].length === 1 ? grid[0][0] : null;
      const targetMaxR = singleValue !== null ? maxR : Math.min(rows.length - 1, minR + grid.length - 1);
      const targetMaxC = singleValue !== null ? maxC : Math.min(columns.length - 1, minC + (grid[0]?.length || 1) - 1);

      const edits = [];
      for (let r = minR; r <= targetMaxR; r++) {
        for (let c = minC; c <= targetMaxC; c++) {
          const row = rows[r]; const column = columns[c];
          if (!row || !column || !isRowCellEditable(row, column, categoriesMeta)) continue;
          const raw = singleValue !== null ? singleValue : grid[(r - minR) % grid.length][(c - minC) % grid[0].length];
          const coerced = coerceValue(raw, column);
          if (coerced !== undefined) {
            edits.push({ rowKey: row.rowKey, kind: row.kind, id: row.productId, combinationKey: row.combinationKey, colKey: column.key, value: coerced });
          }
        }
      }
      if (edits.length) onCellsChange(edits);
      setSelection({ r1: minR, c1: minC, r2: targetMaxR, c2: targetMaxC });
    };

    document.addEventListener('copy', onCopy);
    document.addEventListener('paste', onPaste);
    return () => { document.removeEventListener('copy', onCopy); document.removeEventListener('paste', onPaste); };
  }, [selection, rows, columns, onCellsChange, categoriesMeta]);

  const isSelected = (r, c) => {
    if (!selection) return false;
    const [minR, maxR] = [Math.min(selection.r1, selection.r2), Math.max(selection.r1, selection.r2)];
    const [minC, maxC] = [Math.min(selection.c1, selection.c2), Math.max(selection.c1, selection.c2)];
    return r >= minR && r <= maxR && c >= minC && c <= maxC;
  };
  const isFocus = (r, c) => selection && selection.r2 === r && selection.c2 === c;

  const renderStaticCell = (row, column) => {
    if (column.key === 'image') {
      const media = row.values.media || [];
      const primaryIdx = row.values.primary_image_index ?? 0;
      const url = media.length ? getImageUrl(media[Math.min(primaryIdx, media.length - 1)]) : null;
      return url ? (
        <img src={url} alt="" className="w-8 h-8 rounded object-cover" data-testid="bulk-editor-thumb" />
      ) : (
        <div className="w-8 h-8 rounded bg-gray-100 text-[10px] text-gray-400 flex items-center justify-center">IMG</div>
      );
    }
    if (row.kind === 'variant') {
      if (column.key === 'title') {
        return <span className="text-sm text-gray-500 pl-6">↳ {row.values.title}</span>;
      }
      if (!['sku', 'price', 'inventory', 'status'].includes(column.key)) {
        return <span className="text-gray-300">—</span>;
      }
    }
    if (column.key === 'title' && row.hasVariants) {
      return (
        <button
          type="button"
          className="flex items-center gap-1 text-left w-full"
          onClick={(e) => { e.stopPropagation(); onExpandToggle(row.productId); }}
          data-testid={`bulk-editor-expand-${row.productId}`}
        >
          {row.isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="truncate">{row.values.title}</span>
        </button>
      );
    }
    if (column.key === 'status' && row.kind === 'variant') {
      return <span className={row.values.status ? 'text-green-600' : 'text-gray-400'}>{row.values.status ? 'Available' : 'Unavailable'}</span>;
    }
    if (column.key === 'license_status') {
      const s = row.values.license_status;
      const exp = row.values.license_expiry;
      if (!s) return <span className="text-gray-300">—</span>;
      const map = {
        verified: { cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', label: exp ? `Verified · Exp: ${exp}` : 'Verified' },
        failed: { cls: 'text-red-700 bg-red-50 border-red-200', label: 'Verification Failed' },
        unverified: { cls: 'text-gray-500 bg-gray-50 border-gray-200', label: 'Not Verified' },
      };
      const m = map[s] || map.unverified;
      return <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full border ${m.cls}`}>{m.label}</span>;
    }
    const val = row.values[column.key];
    if (column.type === 'select') return <span>{optionLabel(column, val) || <span className="text-gray-300">—</span>}</span>;
    if (column.type === 'boolean') return <span>{val ? 'Yes' : 'No'}</span>;
    return <span className="truncate block">{val === null || val === undefined || val === '' ? <span className="text-gray-300">—</span> : String(val)}</span>;
  };

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className="outline-none select-none overflow-auto border rounded-lg bg-white"
      style={{ maxHeight: 'calc(100vh - 320px)' }}
      data-testid="bulk-editor-grid"
    >
      <table className="border-collapse" style={{ tableLayout: 'fixed' }}>
        <thead className="sticky top-0 z-20 bg-gray-50">
          <tr>
            {columns.map((col, c) => (
              <th
                key={col.key}
                className="border-b border-r px-2 py-2 text-left text-xs font-semibold text-gray-600 whitespace-nowrap"
                style={{ width: col.width, minWidth: col.width }}
              >
                {col.label}
                {col.key === 'buying_mode' && <Lock size={10} className="inline ml-1 text-amber-500" title="Locked cells indicate an admin category policy" />}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={row.rowKey} className={row.kind === 'variant' ? 'bg-gray-50/60' : (r % 2 ? 'bg-white' : 'bg-gray-50/30')}>
              {columns.map((col, c) => {
                const editable = isRowCellEditable(row, col, categoriesMeta);
                const dirty = dirtyKeys.has(`${row.rowKey}::${col.key}`);
                const editing = editingCell && editingCell.r === r && editingCell.c === c;
                return (
                  <td
                    key={col.key}
                    onMouseDown={() => handleMouseDown(r, c)}
                    onMouseEnter={() => handleMouseEnter(r, c)}
                    onDoubleClick={() => editable && startEdit(r, c)}
                    data-testid={`bulk-editor-cell-${row.rowKey}-${col.key}`}
                    className={`border-r border-b px-2 py-1.5 text-sm relative ${isSelected(r, c) ? 'bg-indigo-50' : ''} ${isFocus(r, c) ? 'ring-2 ring-indigo-500 ring-inset z-10' : ''} ${dirty ? 'bg-amber-50' : ''} ${!editable ? 'cursor-default' : 'cursor-cell'}`}
                    style={{ width: col.width, minWidth: col.width }}
                  >
                    {editing ? (
                      col.type === 'select' ? (
                        <select
                          ref={inputRef}
                          autoFocus
                          value={editValue}
                          onChange={(e) => { setEditValue(e.target.value); commitEdit(e.target.value); }}
                          onBlur={() => commitEdit(editValue)}
                          className="w-full h-7 text-sm border rounded px-1"
                        >
                          <option value="">—</option>
                          {(col.options || []).map((o) => {
                            const value = typeof o === 'string' ? o : o.value;
                            const label = typeof o === 'string' ? o : o.label;
                            return <option key={value} value={value}>{label}</option>;
                          })}
                        </select>
                      ) : (
                        <input
                          ref={inputRef}
                          autoFocus
                          type={col.type === 'number' ? 'number' : 'text'}
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => commitEdit(editValue)}
                          className="w-full h-7 text-sm border rounded px-1"
                        />
                      )
                    ) : (row.kind === 'variant' && col.key === 'status') ? (
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!row.values.status}
                          onChange={(e) => onCellsChange([{ rowKey: row.rowKey, kind: row.kind, id: row.productId, combinationKey: row.combinationKey, colKey: 'status', value: e.target.checked }])}
                        />
                        <span className={row.values.status ? 'text-green-600' : 'text-gray-400'}>{row.values.status ? 'Available' : 'Unavailable'}</span>
                      </label>
                    ) : (
                      renderStaticCell(row, col)
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default BulkEditorGrid;
